import { cn } from "@/lib/utils";
import { FilterOptions } from "@/types/FilterOptions";
import { SearchFilterV4 } from "@/types/SearchFilter";
import { Button } from "@components/ui/button";
import { Trans } from "@lingui/react/macro";
import ChannelTypes from "./partials/ChannelTypes";
import ChannelNames from "./partials/ChannelNames";
import InsightTypes from "./partials/InsightTypes";
import Sentiments from "./partials/Sentiments";
import Languages from "./partials/Languages";
import MentionKinds from "./partials/MentionKinds";
import { PlusIcon } from "lucide-react";
import SearchFilterComponent from "@components/SearchFilter";
import Reach from "./partials/Reach";
import { Dispatch, Fragment, memo, SetStateAction } from "react";
import { FilterBarKey } from "./filterOrder";
import FreeText from "./partials/FreeText";

function SearchFilterBar({
  setIsFreeTextOpen,
  setIsReachOpen,
  innerClassName,
  filterOptions,
  filterOrder,
  className,
  onChange,
  value,
  ...rest
}: {
  onChange?: (value: SearchFilterV4, clear?: boolean) => void;
  setIsFreeTextOpen?: Dispatch<SetStateAction<boolean>>;
  setIsReachOpen?: Dispatch<SetStateAction<boolean>>;
  value: Partial<SearchFilterV4>;
  filterOptions: FilterOptions;
  filterOrder: FilterBarKey[];
  innerClassName?: string;
} & React.ComponentProps<"div">) {
  // Helpers
  const isDirty = filterOrder.length > 0;

  const filterComponents: Record<FilterBarKey, React.ReactNode> = {
    insight_types: (
      <InsightTypes
        options={filterOptions.filter_insight_types}
        value={value.insight_types}
        onChange={onChange}
      />
    ),
    channel_type: (
      <ChannelTypes
        options={filterOptions.filter_sources}
        value={value.channel_type}
        onChange={onChange}
      />
    ),
    channel_name: (
      <ChannelNames
        options={filterOptions.filter_channels}
        value={value.channel_name}
        onChange={onChange}
      />
    ),
    sentiment: (
      <Sentiments
        options={filterOptions.filter_sentiments}
        value={value.sentiment}
        onChange={onChange}
      />
    ),
    language: (
      <Languages
        options={filterOptions.filter_languages}
        value={value.language}
        onChange={onChange}
      />
    ),
    mention_kind: (
      <MentionKinds onChange={onChange} value={value.mention_kind} />
    ),
    reach: (
      <Reach
        value={value.min_reach_threshold}
        setIsReachOpen={setIsReachOpen}
        onChange={onChange}
      />
    ),
    freetext: (
      <FreeText
        setIsFreeTextOpen={setIsFreeTextOpen}
        value={value.freetext}
        onChange={onChange}
      />
    ),
  };

  if (!isDirty) {
    return null;
  }

  return (
    <div
      {...rest}
      className={cn(onChange && "bg-muted rounded-lg p-2", className)}
    >
      <div
        className={cn(
          "flex items-center flex-wrap gap-1.75 max-w-350",
          innerClassName
        )}
      >
        {filterOrder.map((key) => (
          <Fragment key={key}>{filterComponents[key]}</Fragment>
        ))}

        {onChange && (
          <SearchFilterComponent
            setIsFreeTextOpen={setIsFreeTextOpen}
            setIsReachOpen={setIsReachOpen}
            filterOptions={filterOptions}
            onChange={onChange}
            value={value}
            align="start"
          >
            <Button size="icon-xs" variant="ghost">
              <PlusIcon />
              <span className="sr-only">
                <Trans>Lägg till filter</Trans>
              </span>
            </Button>
          </SearchFilterComponent>
        )}

        {isDirty && onChange && (
          <Button
            data-testid="filter-bar--clear"
            size="xs"
            variant="ghost"
            type="button"
            className="ml-auto"
            onClick={() => {
              onChange(
                { start_date: value.start_date, end_date: value.end_date },
                true
              );
            }}
          >
            <Trans>Återställ</Trans>
          </Button>
        )}
      </div>
    </div>
  );
}

export default memo(SearchFilterBar);
